import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Star, Sparkles, X, Pen, Upload, Play } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const PERIODS = [
  { key: "newest", label: "Newest" },
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
];

export default function Reviews() {
  const [period, setPeriod] = useState("newest");
  const [category, setCategory] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openDlg, setOpenDlg] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "", media: [] });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadReviews = () => {
    const params = {};
    if (period !== "newest") params.period = period;
    if (category) params.category = category;
    api.get("/reviews", { params }).then(r => setReviews(r.data));
  };

  useEffect(() => { loadReviews(); }, [period, category]); // eslint-disable-line

  useEffect(() => {
    const params = period === "newest" ? {} : { period };
    api.get("/reviews/categories", { params }).then(r => setCategories(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="reviews-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <p className="label-eyebrow">In the Client's Words</p>
          <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3 mb-2">Reviews</h1>
        </div>
        <button onClick={() => setOpenDlg(true)} className="btn-clay" data-testid="write-review-btn">
          <Pen className="w-4 h-4" strokeWidth={1.5} /> Write a Review
        </button>
      </div>
      <p className="text-[#5C5C5C] max-w-2xl mb-12">
        Reviews are open to everyone — customers, visitors, and industry peers.
        Sub-categories are auto-generated by our AI classifier from the review content itself.
      </p>

      {/* Period filter */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="reviews-periods">
        {PERIODS.map(p => (
          <button
            key={p.key}
            onClick={() => { setPeriod(p.key); setCategory(null); }}
            className={`px-5 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
              period === p.key
                ? "bg-[#1E2C5A] text-white border-[#1E2C5A]"
                : "border-[#1A1A1A]/30 text-[#5C5C5C] hover:border-[#1E2C5A]"
            }`}
            data-testid={`period-${p.key}`}
          >{p.label}</button>
        ))}
      </div>

      {/* AI Categories */}
      {categories.length > 0 && (
        <div className="mb-14" data-testid="reviews-ai-tags">
          <p className="label-eyebrow mb-3 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#C9A968]" strokeWidth={1.5} />
            AI Sub-categories
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCategory(null)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                !category ? "bg-[#C9A968] text-[#1E2C5A]" : "bg-[#F0EFEA] text-[#5C5C5C] hover:bg-[#C9A968]/50"
              }`}
              data-testid="category-all"
            >All</button>
            {categories.map(c => (
              <button
                key={c.category}
                onClick={() => setCategory(c.category)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                  category === c.category ? "bg-[#C9A968] text-[#1E2C5A]" : "bg-[#F0EFEA] text-[#5C5C5C] hover:bg-[#C9A968]/50"
                }`}
                data-testid={`category-${c.category}`}
              >{c.category} <span className="text-[10px] opacity-60 ml-1">{c.count}</span></button>
            ))}
          </div>
        </div>
      )}

      {reviews.length === 0 && (
        <p className="text-[#8A8A8A] text-center py-20">No reviews match your filters.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {reviews.map(r => (
          <div key={r.id} className="border border-[#1A1A1A]/10 p-6 fade-up" data-testid={`review-${r.id}`}>
            <div className="flex items-center gap-1 mb-3">
              {[1,2,3,4,5].map(i => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${i <= r.rating ? "fill-[#C9A968] text-[#C9A968]" : "text-[#E5E5E5]"}`}
                  strokeWidth={1.5}
                />
              ))}
            </div>
            <p className="font-editorial text-lg leading-snug mb-4">&quot;{r.comment}&quot;</p>
            {r.media && r.media.length > 0 && (
              <div className="flex gap-2 mb-4" data-testid={`review-media-${r.id}`}>
                {r.media.slice(0, 4).map((u, i) => (
                  u.match(/\.(mp4|mov|webm|mkv)$/i) ? (
                    <video key={`${r.id}-${u}-${i}`} src={toAbs(u)} controls className="w-16 h-16 object-cover bg-[#F0EFEA]" />
                  ) : (
                    <img key={`${r.id}-${u}-${i}`} src={toAbs(u)} alt="review" className="w-16 h-16 object-cover bg-[#F0EFEA]" />
                  )
                ))}
                {r.media.length > 4 && <span className="text-xs text-[#8A8A8A] self-center ml-1">+{r.media.length - 4}</span>}
              </div>
            )}
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#1E2C5A] font-medium">— {r.customer_name}</span>
              <span className="text-[#8A8A8A]">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
            {r.ai_categories?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-4 pt-4 border-t border-[#1A1A1A]/10">
                {r.ai_categories.map(t => (
                  <span key={t} className="text-[10px] uppercase tracking-wider text-[#C9A968] bg-[#1E2C5A]/5 px-2 py-0.5">{t}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="text-center mt-16 border-t border-[#1A1A1A]/10 pt-10">
        <p className="text-sm text-[#5C5C5C]">Have you experienced TileRoom? Share your thoughts.</p>
        <button onClick={() => setOpenDlg(true)} className="btn-clay mt-4" data-testid="reviews-cta">
          <Pen className="w-4 h-4" strokeWidth={1.5} /> Write a Review
        </button>
      </div>

      {/* Open Review Modal */}
      {openDlg && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="open-review-modal">
          <div className="bg-[#FAF9F6] max-w-lg w-full p-10 relative fade-up">
            <button onClick={() => setOpenDlg(false)} className="absolute top-4 right-4 text-[#5C5C5C]" data-testid="open-review-close">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <p className="label-eyebrow">Open Review</p>
            <h2 className="font-editorial text-3xl tracking-editorial mt-3 mb-6">Share your thoughts</h2>

            <label className="label-eyebrow block mb-1">Your Name <span className="text-[#8A8A8A] normal-case tracking-normal">(or leave blank for anonymous)</span></label>
            <input className="input-editorial mb-6" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" data-testid="open-review-name" />

            <label className="label-eyebrow block mb-2">Rating</label>
            <div className="flex items-center gap-2 mb-6" data-testid="open-review-stars">
              {[1,2,3,4,5].map(i => (
                <button
                  key={i} type="button"
                  onClick={() => setForm(f => ({ ...f, rating: i }))}
                  className={`transition-colors ${i <= form.rating ? "text-[#C9A968]" : "text-[#E5E5E5]"}`}
                  data-testid={`open-review-star-${i}`}
                >
                  <Star className={`w-8 h-8 ${i <= form.rating ? "fill-[#C9A968]" : ""}`} strokeWidth={1.5} />
                </button>
              ))}
            </div>

            <label className="label-eyebrow block mb-1">Your Review</label>
            <textarea
              rows={5}
              className="w-full bg-transparent border-b border-[#d6d3ce] py-3 outline-none focus:border-[#1E2C5A] text-sm resize-none"
              placeholder="Quality, delivery, service, timing — what stood out?"
              value={form.comment}
              onChange={(e) => setForm(f => ({ ...f, comment: e.target.value }))}
              data-testid="open-review-comment"
            />

            <label className="label-eyebrow block mt-6 mb-2">
              Photos / Videos <span className="text-[#8A8A8A] normal-case tracking-normal">(optional · up to 5 · images ≤5MB · videos ≤30MB)</span>
            </label>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              disabled={uploading || (form.media || []).length >= 5}
              onChange={async (e) => {
                const files = Array.from(e.target.files || []).slice(0, 5 - (form.media || []).length);
                if (!files.length) return;
                setUploading(true);
                try {
                  const uploaded = [];
                  for (const f of files) {
                    const fd = new FormData();
                    fd.append("file", f);
                    const { data } = await api.post("/uploads/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    uploaded.push({ url: data.url, kind: data.kind });
                  }
                  setForm(f => ({ ...f, media: [...(f.media || []), ...uploaded] }));
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Upload failed");
                } finally {
                  setUploading(false);
                  e.target.value = "";
                }
              }}
              className="block w-full text-sm mb-3 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#1E2C5A] file:text-white file:uppercase file:text-xs file:tracking-wider disabled:opacity-50"
              data-testid="open-review-media-input"
            />
            {uploading && <p className="text-xs text-[#C9A968] mb-2">Uploading…</p>}
            {(form.media || []).length > 0 && (
              <div className="grid grid-cols-5 gap-2 mb-4">
                {form.media.map((m, i) => (
                  <div key={m.url || `media-${i}`} className="relative bg-[#F0EFEA]" data-testid={`review-media-preview-${i}`}>
                    {m.kind === "video" ? (
                      <video src={toAbs(m.url)} className="w-full aspect-square object-cover" muted />
                    ) : (
                      <img src={toAbs(m.url)} alt="review media" className="w-full aspect-square object-cover" />
                    )}
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, media: f.media.filter((_, idx) => idx !== i) }))}
                      className="absolute top-0 right-0 bg-red-700 text-white w-5 h-5 flex items-center justify-center text-xs"
                      data-testid={`remove-review-media-${i}`}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={async () => {
                if (!form.comment.trim()) { toast.error("Please write your review"); return; }
                setBusy(true);
                try {
                  await api.post("/reviews/open", {
                    name: form.name,
                    rating: form.rating,
                    comment: form.comment,
                    media: (form.media || []).map(m => m.url),
                  });
                  toast.success("Thanks! Your review is being analyzed by AI...");
                  setOpenDlg(false);
                  setForm({ name: "", rating: 5, comment: "", media: [] });
                  loadReviews();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Could not submit");
                } finally { setBusy(false); }
              }}
              disabled={busy || uploading}
              className="btn-clay w-full mt-6"
              data-testid="open-review-submit"
            >
              {busy ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
